<template>
  <FixedLoader v-if="loading" />
  <template v-else-if="!loading && data">
    <div class="hero-links hero-links-fixed">
      <transition name="fade" appear>
        <RouterLink :to="{ name: 'story' }" class="group"
          ><span class="link-dot" /> Read</RouterLink
        >
      </transition>
      <transition name="fade" appear>
        <a
          v-if="works?.length"
          href="#works"
          class="group"
          @click.prevent="scrollTo('#works', 96)"
          ><span class="link-dot" />
          <span class="hidden xl:inline"> Available</span> Works</a
        >
      </transition>
    </div>

    <div class="lg:hidden p-8 text-center mt-12 font-serif text-2xl">
      <h1>{{ data.artist?.name }}</h1>
      <h2>
        <span class="italic">{{ data.title }}</span
        ><template v-if="data.year">, {{ data.year }}</template>
      </h2>
    </div>
    <div
      class="wrap"
      :class="[
        { 'zoomed cursor-min': zoomed },
        { 'multiple-images': multipleImages },
      ]"
      :style="{ minHeight: `${minHeight}px` }"
    >
      <div class="images">
        <div class="image" v-for="image of data.images" :key="image.large.src">
          <ImageWrap
            :image="image.large"
            :class="zoomed ? 'cursor-min' : 'cursor-max'"
            @click="onZoom"
          />
        </div>
      </div>
    </div>
    <div
      class="lg:absolute top-0 right-0 w-full lg:w-1/3 z-10 lg:pt-36 lg:pr-24"
      ref="textElement"
    >
      <div class="mx-auto p-8 lg:p-0 max-w-xl lg:max-w-sm">
        <div class="hidden lg:block font-serif text-2xl">
          <h1>{{ data.artist?.name }}</h1>
          <h2>
            <span class="italic">{{ data.title }}</span
            ><template v-if="data.year">, {{ data.year }}</template>
          </h2>
        </div>
        <div class="lg:mt-12 text-center lg:text-left">
          <div v-for="spec of specs" :key="spec">{{ spec }}</div>
          <div v-if="data.notes">
            <BlockContent :blocks="data.notes" />
          </div>
          <div v-if="data.price">£{{ data.price }}</div>
        </div>
        <div class="mt-4 text-center lg:text-left">
          {{ data.available ? 'Available' : 'Unavailable' }}
        </div>
        <div v-if="data.description" class="mt-12">
          <BlockContent :blocks="data.description" />
        </div>
        <div v-if="data.available" class="mt-12">
          <WorkFormInputs :work="data" />
        </div>
      </div>
    </div>
    <WorksGrid id="works" :works="works || []" />
  </template>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import animateScrollTo from 'animated-scroll-to';
import { useRoute } from 'vue-router';
import getWork from '@/sanity/requests/work';
import WorkFormInputs from '@/components/WorkFormInputs.vue';
import WorksGrid from '@/components/WorksGrid.vue';

export default defineComponent({
  name: 'Work',
  components: {
    WorkFormInputs,
    WorksGrid,
  },
  setup() {
    const route = useRoute();
    const zoomed = ref(false);
    const resetState = () => {
      zoomed.value = false;
    };
    watch(() => route.params, resetState);

    let { data, loading } = getWork(route);

    const specs = computed(() => {
      if (!data.value) return [];
      const dimensions = Object.values(data.value.dimensions || {})
        .map((i) => `${i}`)
        .join(' × ');
      return [data.value.medium, dimensions ? `${dimensions} cm` : ''].filter(
        (i) => i
      );
    });

    const works = computed(() => data.value?.works || []);

    const onZoom = async (e: Event) => {
      zoomed.value = !zoomed.value;
      if (zoomed.value === true) {
        await nextTick();
        const target = e.target as HTMLElement;
        const pos = target.getBoundingClientRect().top + window.scrollY;
        window.scrollTo(0, pos);
      } else {
        window.scrollTo(0, 0);
      }
    };

    const multipleImages = computed(() => (data.value?.images.length || 0) > 1);

    const scrollTo = (selector: string, offset = 0) => {
      const [el] = document.querySelectorAll(selector);
      if (el !== null && el instanceof HTMLElement) {
        animateScrollTo(el.offsetTop - offset, {
          cancelOnUserAction: false,
          maxDuration: 1000,
        });
      }
    };

    // Enforce minimum height of image section
    // Based on text section height (absolutely positioned)
    const textElement = ref<HTMLElement | null>(null);
    const minHeight = ref(0);

    const setMinHeight = async () => {
      await nextTick();
      const textHeight = textElement.value?.offsetHeight || 0;
      minHeight.value = textHeight;
    };

    // set when loaded, and on resize
    watch(() => loading.value, setMinHeight);
    onMounted(() => window.addEventListener('resize', setMinHeight));
    onBeforeUnmount(() => window.removeEventListener('resize', setMinHeight));

    return {
      data,
      loading,
      minHeight,
      multipleImages,
      onZoom,
      textElement,
      scrollTo,
      specs,
      works,
      zoomed,
    };
  },
});
</script>

<style lang="postcss" scoped>
.wrap {
  @apply w-full lg:pt-12 xl:px-24;
}

.wrap.multiple-images {
  @apply px-12;
}

.wrap.zoomed {
  @apply lg:w-full p-0;
}

.zoomed .images {
  @apply lg:w-full;
}

.images {
  @apply flex flex-wrap;
  @apply w-full lg:w-2/3;
}

.image {
  @apply w-full px-24 py-4 lg:py-24;
}

.multiple-images .image {
  @apply w-full lg:w-1/2 p-4 lg:p-12;
}

.zoomed .image {
  @apply w-full p-0;
}
</style>
